/**
 * Admin Generation Page Component
 *
 * AI-powered question generation interface for creating new exam questions
 * with custom parameters. Supports multiple modules (reading, listening, writing, speaking)
 * and various sections with specialized generation logic.
 *
 * Route: `/admin/generation`
 * Requires authentication: Yes (admin only)
 *
 * @module AdminGenerationPage
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlusCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import ContextGroup from '@/components/admin/ContextGroup';
import MissingParametersDialog from '@/components/admin/MissingParametersDialog';
import { groupQuestionsByContext } from '@/utils/questionHelpers';
import { useFormStateClear } from '@/hooks/useFormStateClear';
import {
  fetchSampleQuestions,
  generateAudioForTranscripts,
  generateQuestions,
  saveGeneratedContext,
} from '@/lib/adminGenerationApi';
import { logger } from '@/lib/logger';
import { usesTextOnlySpeakingContext } from '@/utils/adminGenerationContext';

export default function AdminGenerationPage() {
  // Context generation form state
  const [generationForm, setGenerationForm] = useState({
    examType: '',
    level: '',
    module: '',
    section: '',
    model: '',
    contextCount: 5,
    sampleCount: 3,
  });

  const [sampleQuestions, setSampleQuestions] = useState<any[]>([]);
  const [loadingSampleData, setLoadingSampleData] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<any[]>([]);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [generatedTranscripts, setGeneratedTranscripts] = useState<any[]>([]);
  const [generatingAudio, setGeneratingAudio] = useState(false);

  // Editing state
  const [editingContext, setEditingContext] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [editingContextData, setEditingContextData] = useState<any>(null);
  const [editingQuestionData, setEditingQuestionData] = useState<any>(null);

  // Missing parameters dialog state
  const [showMissingParamsDialog, setShowMissingParamsDialog] = useState(false);
  const [questionsNeedingParams, setQuestionsNeedingParams] = useState<any[]>(
    [],
  );
  const [pendingContextName, setPendingContextName] = useState<string | null>(
    null,
  );

  // Delete dialog state
  const [showDeleteQuestionDialog, setShowDeleteQuestionDialog] =
    useState(false);
  const [showDeleteContextDialog, setShowDeleteContextDialog] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(
    null,
  );
  const [contextToDelete, setContextToDelete] = useState<string | null>(null);

  // Hook for clearing form state
  const clearGeneratedContent = useFormStateClear(
    setGeneratedQuestions,
    setGeneratedTranscripts,
    setSampleQuestions,
  );

  // Load Sample Data function
  const handleLoadSampleData = async () => {
    if (
      !generationForm.examType ||
      !generationForm.level ||
      !generationForm.module ||
      !generationForm.section
    ) {
      toast.error(
        'Please fill in all required fields (Exam Type, Level, Module, Section)',
      );
      return;
    }

    try {
      setLoadingSampleData(true);

      const data = await fetchSampleQuestions({
        examType: generationForm.examType,
        level: generationForm.level,
        module: generationForm.module,
        section: generationForm.section,
        sampleCount: generationForm.sampleCount || 3,
      });

      if (data) {
        setSampleQuestions(data.questions || []);
      } else {
        toast.error('Error loading sample data');
      }
    } catch (error) {
      logger.error('Error loading sample data', error);
      toast.error('Failed to load sample data');
    } finally {
      setLoadingSampleData(false);
    }
  };

  // Generate Audio for Existing Transcripts (On-Demand)
  const handleGenerateAudio = async () => {
    if (generatedTranscripts.length === 0) {
      toast.error('No transcripts to generate audio for');
      return;
    }

    setGeneratingAudio(true);
    toast.info('Generating audio... Please wait');
    try {
      logger.debug('Generating audio for transcripts', {
        transcriptsCount: generatedTranscripts.length,
      });

      const data = await generateAudioForTranscripts({
        transcripts: generatedTranscripts,
        parameters: {
          level: generationForm.level,
          module: generationForm.module,
          section: generationForm.section,
          examType: generationForm.examType,
          model: generationForm.model || 'openai',
          voice: 'alloy',
        },
      });
      logger.debug('Generated audio contexts', { count: data.contexts.length });

      const audioByName = new Map(
        (data.contexts as any[])
          .filter((ctx) => ctx?.name)
          .map((ctx) => [ctx.name, ctx]),
      );

      const updatedQuestions = generatedQuestions.map((q) => {
        const contextName = q.context_name || q.context?.name;
        const audioContext = audioByName.get(contextName) as any;
        if (!audioContext) return q;

        return {
          ...q,
          context_id: audioContext.id,
          binary_content_base64: audioContext.audioBase64,
          mime_type: audioContext.mimeType,
          file_extension: audioContext.fileExtension,
          hasAudio: true,
        };
      });

      setGeneratedQuestions(updatedQuestions);
      toast.success(
        `Success! Generated audio for ${data.contexts.length} contexts`,
      );
    } catch (error: any) {
      logger.error('Audio generation error', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Generate Audio for a Specific Context
  const handleGenerateAudioForContext = async (contextName: string) => {
    const contextQuestions = generatedQuestions.filter(
      (q) => (q.context_name || q.context?.name) === contextName,
    );

    if (contextQuestions.length === 0 || !contextQuestions[0].transcript) {
      toast.error('No transcript found for this context');
      return;
    }

    const transcript = {
      name: contextName,
      transcript: contextQuestions[0].transcript,
      scenario: contextQuestions[0].scenario,
    };

    setGeneratingAudio(true);
    toast.info(`Generating audio for ${contextName}... Please wait`);
    try {
      logger.debug('Generating audio for context', { contextName });

      const data = await generateAudioForTranscripts({
        transcripts: [transcript],
        parameters: {
          level: generationForm.level,
          module: generationForm.module,
          section: generationForm.section,
          examType: generationForm.examType,
          model: generationForm.model || 'openai',
          contextIndex: 0,
          contextName: contextName,
        },
      });
      logger.debug('Generated audio for context', { contextName });

      const audioContext = data.contexts[0] as any;

      setGeneratedQuestions((prev) =>
        prev.map((q) => {
          if ((q.context_name || q.context?.name) === contextName) {
            return {
              ...q,
              context_id: audioContext.id,
              binary_content_base64: audioContext.audioBase64,
              mime_type: audioContext.mimeType,
              file_extension: audioContext.fileExtension,
              hasAudio: true,
            };
          }
          return q;
        }),
      );

      const hasExistingAudio = contextQuestions[0].binary_content_base64;
      toast.success(
        `Success! ${hasExistingAudio ? 'Regenerated' : 'Generated'} audio for "${contextName}"`,
      );
    } catch (error: any) {
      logger.error('Audio generation error', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const isTextOnlySpeakingPrompt = (): boolean =>
    usesTextOnlySpeakingContext(generationForm);

  const moduleUsesGeneratedTranscripts = (): boolean =>
    !isTextOnlySpeakingPrompt() &&
    (generationForm.module === 'listening' || generationForm.module === 'speaking');

  const getContextContent = (context: any): string =>
    context?.content || context?.text_content || context?.transcript || '';

  const buildGeneratedTranscripts = (contexts: any[]): any[] => {
    if (!moduleUsesGeneratedTranscripts()) {
      return [];
    }

    return contexts
      .map((context: any) => {
        const transcript = context?.transcript || getContextContent(context);
        if (!context?.name || !transcript) {
          return null;
        }
        return {
          name: context.name,
          transcript,
          scenario: context.scenario,
          theme: context.theme,
        };
      })
      .filter(Boolean);
  };

  const getGenerationModelFromResponse = (data: any): string | null =>
    data?.generation_info?.model || data?.generationInfo?.model || null;

  const getGeneratedQuestionModel = (question: any): string | null =>
    question?.generationModel ||
    question?.generation_model ||
    question?.context?.generationModel ||
    question?.context?.generation_model ||
    null;

  const enrichQuestionsWithGeneratedContexts = (
    questions: any[],
    contexts: any[],
    generationModel: string | null,
  ): any[] => {
    const contextMap = new Map(
      contexts
        .filter((context: any) => context?.name)
        .map((context: any) => [context.name, context]),
    );

    return questions.map((question: any) => {
      const matchingContext = contextMap.get(question.context_name) as any;
      const contextContent =
        getContextContent(matchingContext) || question.context_content || '';
      const transcript =
        matchingContext?.transcript ||
        (moduleUsesGeneratedTranscripts() ? contextContent : question.transcript);
      const resolvedGenerationModel =
        generationModel ||
        question.generationModel ||
        question.generation_model ||
        matchingContext?.generationModel ||
        matchingContext?.generation_model ||
        null;

      const baseEnriched = {
        ...question,
        generationModel: resolvedGenerationModel,
        generation_model: resolvedGenerationModel,
        context_content: contextContent,
        context: {
          ...(question.context || {}),
          generationModel: resolvedGenerationModel,
        },
        scenario: matchingContext?.scenario ?? question.scenario,
        theme: matchingContext?.theme ?? question.theme,
      };

      // Text-only speaking prompts use plain text contexts (like reading) — never audio.
      // Force context_type: 'text' so Save works without audio and the UI does not
      // render a "Transcript" header. Parameters (preparationTime, recordingDuration)
      // are NOT auto-defaulted here so MissingParametersDialog still prompts the user.
      if (isTextOnlySpeakingPrompt()) {
        return {
          ...baseEnriched,
          context_type: 'text',
        };
      }

      return {
        ...baseEnriched,
        ...(transcript
          ? {
              context_type: question.context_type || 'audio',
              transcript,
              hasAudio: question.hasAudio ?? false,
            }
          : {}),
      };
    });
  };

  // Helper: Build generation parameters
  const buildGenerationParameters = (): {
    examType: string;
    level: string;
    module: string;
    section: number;
    model: string;
    contextCount: number;
  } => {
    const requestedCount = Math.max(1, generationForm.contextCount || 1);

    return {
      examType: generationForm.examType,
      level: generationForm.level,
      module: generationForm.module,
      section: Number.parseInt(generationForm.section),
      model: generationForm.model || 'openai',
      contextCount: requestedCount,
    };
  };

  // Helper: Build success message
  const buildSuccessMessage = (
    questionCount: number,
    contextCount: number,
    hasGeneratedTranscripts: boolean,
  ): string => {
    let message = `Successfully generated ${contextCount} contexts and ${questionCount} questions!`;
    if (hasGeneratedTranscripts) {
      message += ' Click "Generate Audio" to create audio files.';
    }
    return message;
  };

  // Helper: Process successful generation response
  const processGenerationResponse = async (data: any): Promise<void> => {
    const contexts = data.contexts || [];
    const generationModel = getGenerationModelFromResponse(data);
    const transcripts = buildGeneratedTranscripts(contexts);
    const enrichedQuestions = enrichQuestionsWithGeneratedContexts(
      data.questions || [],
      contexts,
      generationModel,
    );

    setGeneratedTranscripts(transcripts);
    setGeneratedQuestions(enrichedQuestions);
    const questionCount = enrichedQuestions.length;
    const contextCount = contexts.length;

    const successMessage = buildSuccessMessage(
      questionCount,
      contextCount,
      transcripts.length > 0,
    );
    toast.success(successMessage);
  };

  // Generate Questions function
  const handleGenerateQuestions = async () => {
    if (
      !generationForm.examType ||
      !generationForm.level ||
      !generationForm.module ||
      !generationForm.section
    ) {
      toast.error(
        'Please fill in all required fields (Exam Type, Level, Module, Section)',
      );
      return;
    }

    try {
      setGeneratingQuestions(true);
      toast.info('Generating contexts and questions... Please wait');

      const parameters = buildGenerationParameters();

      const data = await generateQuestions({
        sampleQuestions: sampleQuestions,
        parameters: parameters,
      });

      const firstQuestion = data.questions?.[0] as any;
      logger.debug('AI Generation Response', {
        contextsCount: data.contexts?.length || 0,
        contexts: data.contexts,
        questionsCount: data.questions?.length || 0,
        firstQuestion,
        firstQuestionContextContent: firstQuestion?.context_content,
        firstQuestionContextName: firstQuestion?.context_name,
      });

      await processGenerationResponse(data);
    } catch (error: any) {
      logger.error('Error generating questions', error);
      const errorMessage =
        error?.message || 'Network error or server unavailable';
      toast.error(`Error generating questions: ${errorMessage}`);
    } finally {
      setGeneratingQuestions(false);
    }
  };

  // Edit functions
  const handleEditContext = (contextName: string) => {
    const contextGroups: any = {};
    generatedQuestions.forEach((question) => {
      const ctxName =
        question.context_name || question.context?.name || 'Generated Context';
      if (!contextGroups[ctxName]) {
        contextGroups[ctxName] = {
          name: ctxName,
          content: question.context_content || '',
          questions: [],
        };
      }
      if (ctxName === contextName) {
        contextGroups[ctxName].questions.push(question);
      }
    });

    setEditingContext(contextName);
    setEditingContextData({
      name: contextName,
      content: contextGroups[contextName]?.content || '',
    });
  };

  const handleSaveContext = () => {
    if (!editingContext || !editingContextData) return;

    setGeneratedQuestions((prev) =>
      prev.map((question) => {
        const ctxName = question.context_name || question.context?.name;
        if (ctxName === editingContext) {
          return {
            ...question,
            context_name: editingContextData.name,
            context_content: editingContextData.content,
            context: {
              ...question.context,
              name: editingContextData.name,
            },
          };
        }
        return question;
      }),
    );

    setEditingContext(null);
    setEditingContextData(null);
  };

  const checkMissingParameters = (questions: any[]): any[] => {
    return questions.filter((q) => {
      if (q.type === 'audio-recording') {
        return (
          !q.recordingDuration ||
          q.recordingDuration === undefined ||
          q.recordingDuration === null
        );
      }
      // live-conversation questions don't need a recordingDuration.
      return false;
    });
  };

  const performSave = async (contextName: string, questionsToSave: any[]) => {
    try {
      const firstQuestion = questionsToSave[0];
      const isAudioContext = firstQuestion.context_type === 'audio';

      const contextData = {
        name: contextName,
        content: firstQuestion.context_content || '',
        ...(isAudioContext && {
          audioBase64: firstQuestion.binary_content_base64,
          transcript: firstQuestion.transcript,
          mimeType: firstQuestion.mime_type || 'audio/mpeg',
          fileExtension: firstQuestion.file_extension || 'mp3',
        }),
      };

      if (contextData.content === '' || contextData.content == null) {
        toast.error('Context content is missing');
        return;
      }

      if (isAudioContext && (contextData.audioBase64 === '' || contextData.audioBase64 == null)) {
        toast.error('Audio data is missing. Please generate audio first.');
        return;
      }

      const data = await saveGeneratedContext({
        contextData: contextData,
        questions: questionsToSave,
        generationModel: getGeneratedQuestionModel(firstQuestion),
      });

      toast.success(
        data.message ||
        `Successfully saved context with ${data.questionCount} questions`,
      );

      setGeneratedQuestions((prev) =>
        prev.filter(
          (q) => (q.context_name || q.context?.name) !== contextName,
        ),
      );
    } catch (error: any) {
      logger.error('Error saving context', error);
      toast.error(`Error saving context: ${error.message || 'Network error'}`);
    }
  };

  const handleSaveContextToDatabase = async (contextName: string) => {
    const contextQuestions = generatedQuestions.filter(
      (q) => (q.context_name || q.context?.name) === contextName,
    );

    if (contextQuestions.length === 0) {
      toast.error('No questions found for this context');
      return;
    }

    const questionsNeedingParams = checkMissingParameters(contextQuestions);

    if (questionsNeedingParams.length > 0) {
      setQuestionsNeedingParams(questionsNeedingParams);
      setPendingContextName(contextName);
      setShowMissingParamsDialog(true);
      return;
    }

    await performSave(contextName, contextQuestions);
  };

  // Helper: Update question with missing parameters
  const updateQuestionWithParams = (
    question: any,
    params: { preparationTime: number; recordingDuration: number },
  ): any => {
    if (question.type === 'audio-recording') {
      return {
        ...question,
        preparationTime: question.preparationTime ?? params.preparationTime,
        recordingDuration: question.recordingDuration ?? params.recordingDuration,
      };
    }
    // live-conversation and other types: no recording duration to patch.
    return question;
  };

  // Helper: Find matching updated question
  const findMatchingUpdatedQuestion = (
    question: any,
    updatedQuestions: any[],
  ): any => {
    return updatedQuestions.find(
      (uq) => uq.id === question.id || uq === question,
    );
  };

  // Helper: Update question in generated questions list
  const updateQuestionInList = (
    question: any,
    pendingContextName: string,
    updatedQuestions: any[],
  ): any => {
    const ctxName = question.context_name || question.context?.name;
    if (ctxName === pendingContextName) {
      const updated = findMatchingUpdatedQuestion(question, updatedQuestions);
      return updated || question;
    }
    return question;
  };

  const handleApplyMissingParams = async (params: {
    preparationTime: number;
    recordingDuration: number;
  }) => {
    if (!pendingContextName) return;

    const contextQuestions = generatedQuestions.filter(
      (q) => (q.context_name || q.context?.name) === pendingContextName,
    );

    const updatedQuestions = contextQuestions.map((q) =>
      updateQuestionWithParams(q, params),
    );

    setGeneratedQuestions((prev) =>
      prev.map((q) => updateQuestionInList(q, pendingContextName, updatedQuestions)),
    );

    setShowMissingParamsDialog(false);
    setQuestionsNeedingParams([]);

    await performSave(pendingContextName, updatedQuestions);

    setPendingContextName(null);
  };

  const handleCancelEditContext = () => {
    setEditingContext(null);
    setEditingContextData(null);
  };

  const handleEditQuestion = (questionId: string) => {
    const question = generatedQuestions.find(
      (q) =>
        q.id === questionId ||
        (q.context_name &&
          q.text &&
          `${q.context_name}-${q.text.substring(0, 20)}` === questionId),
    );

    if (question) {
      setEditingQuestion(questionId);
      setEditingQuestionData({
        text: question.text,
        type: question.type,
        options: question.options || [],
        expected_answer: question.expected_answer,
        explanation: question.explanation,
        level: question.level,
        module: question.module,
        section: question.section,
        exam_type: question.exam_type || [generationForm.examType],
      });
    }
  };

  const handleSaveQuestion = () => {
    if (!editingQuestion || !editingQuestionData) return;

    setGeneratedQuestions((prev) =>
      prev.map((question) => {
        const questionId =
          question.id ||
          (question.context_name &&
            question.text &&
            `${question.context_name}-${question.text.substring(0, 20)}`);

        if (questionId === editingQuestion) {
          return {
            ...question,
            ...editingQuestionData,
            options: editingQuestionData.options,
          };
        }
        return question;
      }),
    );

    setEditingQuestion(null);
    setEditingQuestionData(null);
  };

  const handleCancelEditQuestion = () => {
    setEditingQuestion(null);
    setEditingQuestionData(null);
  };

  const openDeleteQuestionDialog = (questionId: string) => {
    setQuestionToDelete(questionId);
    setShowDeleteQuestionDialog(true);
  };

  const handleDeleteQuestion = () => {
    if (!questionToDelete) return;
    setGeneratedQuestions((prev) =>
      prev.filter((question) => {
        const qId =
          question.id ||
          (question.context_name &&
            question.text &&
            `${question.context_name}-${question.text.substring(0, 20)}`);
        return qId !== questionToDelete;
      }),
    );
    setShowDeleteQuestionDialog(false);
    setQuestionToDelete(null);
  };

  const openDeleteContextDialog = (contextName: string) => {
    setContextToDelete(contextName);
    setShowDeleteContextDialog(true);
  };

  const handleDeleteContext = () => {
    if (!contextToDelete) return;
    setGeneratedQuestions((prev) =>
      prev.filter(
        (q) =>
          (q.context_name || q.context?.name) !== contextToDelete,
      ),
    );
    setShowDeleteContextDialog(false);
    setContextToDelete(null);
  };


  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-ui">
            <PlusCircle className="h-5 w-5" />
            Question Generation
          </CardTitle>
          <CardDescription className="font-ui">
            Generate new questions using AI with custom parameters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="exam-type" className="font-ui">
                      Exam Type
                    </Label>
                    <Select
                      value={generationForm.examType}
                      onValueChange={(value) => {
                        setGenerationForm((prev) => ({
                          ...prev,
                          examType: value,
                        }));
                        clearGeneratedContent();
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select exam type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="goethe">
                          <span className="font-ui">Goethe</span>
                        </SelectItem>
                        <SelectItem value="ösd">
                          <span className="font-ui">ÖSD</span>
                        </SelectItem>
                        <SelectItem value="telc">
                          <span className="font-ui">TELC</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="level" className="font-ui">
                      Level
                    </Label>
                    <Select
                      value={generationForm.level}
                      onValueChange={(value) => {
                        setGenerationForm((prev) => ({ ...prev, level: value }));
                        clearGeneratedContent();
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((l) => (
                          <SelectItem key={l} value={l}>
                            <span className="font-ui">{l}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="module" className="font-ui">
                      Module
                    </Label>
                    <Select
                      value={generationForm.module}
                      onValueChange={(value) => {
                        setGenerationForm((prev) => ({ ...prev, module: value }));
                        clearGeneratedContent();
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select module" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reading">
                          <span className="font-ui">Reading</span>
                        </SelectItem>
                        <SelectItem value="listening">
                          <span className="font-ui">Listening</span>
                        </SelectItem>
                        <SelectItem value="writing">
                          <span className="font-ui">Writing</span>
                        </SelectItem>
                        <SelectItem value="speaking">
                          <span className="font-ui">Speaking</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="section" className="font-ui">
                      Section
                    </Label>
                    <Select
                      value={generationForm.section}
                      onValueChange={(value) => {
                        setGenerationForm((prev) => ({
                          ...prev,
                          section: value,
                        }));
                        clearGeneratedContent();
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select section" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((s) => (
                          <SelectItem key={s} value={String(s)}>
                            <span className="font-ui">Section {s}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="model" className="font-ui">
                      Model
                    </Label>
                    <Select
                      value={generationForm.model}
                      onValueChange={(value) => {
                        setGenerationForm((prev) => ({ ...prev, model: value }));
                        clearGeneratedContent();
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openai">
                          <span className="font-ui">OpenAI</span>
                        </SelectItem>
                        <SelectItem value="gemini">
                          <span className="font-ui">Gemini</span>
                        </SelectItem>
                        <SelectItem value="anthropic">
                          <span className="font-ui">Anthropic</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="context-count" className="font-ui">
                      Number of Contexts
                    </Label>
                    <Input
                      id="context-count"
                      type="number"
                      min="1"
                      max="50"
                      placeholder="Enter number of contexts"
                      value={generationForm.contextCount}
                      onChange={(e) =>
                        setGenerationForm((prev) => ({
                          ...prev,
                          contextCount: Number.parseInt(e.target.value) || 5,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="sample-count" className="font-ui">
                      Number of Sample Units
                    </Label>
                    <Input
                      id="sample-count"
                      type="number"
                      min="1"
                      max="20"
                      placeholder="Enter number of sample units"
                      value={generationForm.sampleCount}
                      onChange={(e) =>
                        setGenerationForm((prev) => ({
                          ...prev,
                          sampleCount: Number.parseInt(e.target.value) || 3,
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-1 font-ui">
                      Number of sample question units to load (default: 3)
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={handleLoadSampleData}
                    disabled={loadingSampleData}
                    className="w-full sm:w-auto"
                  >
                    {loadingSampleData ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span className="font-ui">Loading...</span>
                      </>
                    ) : (
                      <span className="font-ui">Load Sample Data</span>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      clearGeneratedContent();
                      setGenerationForm({
                        examType: '',
                        level: '',
                        module: '',
                        section: '',
                        model: '',
                        contextCount: 5,
                        sampleCount: 3,
                      });
                    }}
                  >
                    <span className="font-ui">Reset</span>
                  </Button>
                  <Button
                    className="w-full sm:w-auto"
                    onClick={handleGenerateQuestions}
                    disabled={generatingQuestions}
                  >
                    {generatingQuestions ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span className="font-ui">Generating...</span>
                      </>
                    ) : (
                      <span className="font-ui">Generate Contexts</span>
                    )}
                  </Button>
                </div>
              </div>
          {sampleQuestions.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4 font-ui">
                Sample Questions
              </h3>
              <div className="space-y-6">
                {Object.entries(
                  groupQuestionsByContext(sampleQuestions, 'No Context'),
                ).map(([contextName, questions]) => (
                  <ContextGroup
                    key={contextName}
                    contextName={contextName}
                    questions={questions}
                    variant="sample"
                  />
                ))}
              </div>
            </div>
          )}

          {generatedQuestions.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold font-ui">
                    AI Generated Questions ({generatedQuestions.length} questions)
                  </h3>
                  <Badge variant="secondary" className="font-ui">New</Badge>
                </div>
                {generatedTranscripts.length > 0 && generationForm.module !== 'reading' && (
                  <Button
                    variant="outline"
                    onClick={handleGenerateAudio}
                    disabled={generatingAudio}
                  >
                    {generatingAudio ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span className="font-ui">Generating Audio...</span>
                      </>
                    ) : (
                      <span className="font-ui">Generate Audio for All</span>
                    )}
                  </Button>
                )}
              </div>
              <div className="space-y-6">
                {Object.entries(
                  groupQuestionsByContext(
                    generatedQuestions,
                    'Generated Context',
                  ),
                ).map(([contextName, questions]) => (
                  <ContextGroup
                    key={contextName}
                    contextName={contextName}
                    questions={questions}
                    variant="generated"
                    editingContext={editingContext}
                    editingContextData={editingContextData}
                    onEditContext={() => handleEditContext(contextName)}
                    onSaveContext={handleSaveContext}
                    onCancelEditContext={handleCancelEditContext}
                    onSaveContextToDatabase={() =>
                      handleSaveContextToDatabase(contextName)
                    }
                    onGenerateAudio={
                      generationForm.module !== 'reading'
                        ? () => handleGenerateAudioForContext(contextName)
                        : undefined
                    }
                    onDeleteContext={() => openDeleteContextDialog(contextName)}
                    generatingAudio={generatingAudio}
                    editingQuestion={editingQuestion}
                    onEditQuestion={handleEditQuestion}
                    onSaveQuestion={handleSaveQuestion}
                    onCancelEditQuestion={handleCancelEditQuestion}
                    onDeleteQuestion={openDeleteQuestionDialog}
                    onContextNameChange={(name) =>
                      setEditingContextData((prev) => ({ ...prev, name }))
                    }
                    onContextContentChange={(content) =>
                      setEditingContextData((prev) => ({ ...prev, content }))
                    }
                    editingQuestionData={editingQuestionData}
                    onQuestionTextChange={(text) =>
                      setEditingQuestionData((prev) => ({ ...prev, text }))
                    }
                    onOptionsChange={(options) =>
                      setEditingQuestionData((prev) => ({ ...prev, options }))
                    }
                    onExpectedAnswerChange={(answer) =>
                      setEditingQuestionData((prev) => ({
                        ...prev,
                        expected_answer: answer,
                      }))
                    }
                    onExplanationChange={(explanation) =>
                      setEditingQuestionData((prev) => ({
                        ...prev,
                        explanation,
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card >

      <AlertDialog
        open={showDeleteQuestionDialog}
        onOpenChange={setShowDeleteQuestionDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">Delete Question</AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              Are you sure you want to delete this question? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuestion}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span className="font-ui">Delete</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showDeleteContextDialog}
        onOpenChange={setShowDeleteContextDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">Delete Context</AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              {contextToDelete &&
                `Are you sure you want to delete "${contextToDelete}" and all its questions? This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContext}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span className="font-ui">Delete</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MissingParametersDialog
        open={showMissingParamsDialog}
        onOpenChange={setShowMissingParamsDialog}
        questions={questionsNeedingParams}
        onApply={handleApplyMissingParams}
      />
    </>
  );
}
